import { ServingEnvironment } from "@optima-stride-ai/optima-app-framework";
import { AppName } from "../enum/app-name";

/**
 * Environment Configuration Interface
 * 
 * Defines the structure for all environment configurations.
 * This ensures type safety across development, production, and test environments.
 */
export interface Environment {
  appName: AppName;
  /**
   * User-facing display name (properly cased).
   * An app can have a display name different from its internal registry key.
   * e.g. `'Knowledge Base'` while `appName` is `'hayagriva'`.
   */
  displayName: string;
  /** Stable technical subdomain for hosted URL construction (e.g. 'hayagriva'). Decoupled from display name. */
  subdomain: string;
  appId: string;
  appEnvironment?: ServingEnvironment;
  /**
   * Whether the app is running in production mode
   */
  production: boolean;

  /**
   * Environment name for identification
   */
  name: ServingEnvironment;

  /**
   * Main API URL - localhost for dev/test, relative for production
   * @example 'http://localhost:3000/api' (dev)
   * @example '/api' (prod)
   */
  apiUrl: string;

  /**
   * Collabora Online server URL
   * @example 'http://localhost:9980' (dev)
   * @example '/collabora' (prod)
   */
  collaboraUrl: string;

  /**
   * WOPI protocol endpoint for document editing
   * @example 'http://localhost:3000' (dev)
   * @example '' (prod)
   */
  wopiUrl: string;

  /**
   * Whiteboard proxy URL for diagram editing
   * @example 'http://localhost:5173/wopi' (dev)
   * @example '/wopi' (prod)
   */
  whiteboardProxyUrl: string;

  /**
   * Whether Collabora integration is enabled
   */
  collaboraEnabled: boolean;

  /**
   * Whether debug mode is enabled
   */
  debugMode: boolean;

  /**
   * Platform API URL - ALWAYS HTTPS
   * This URL should NEVER use localhost
   * @readonly Do not modify this URL
   * @example 'https://dev.optimastride.com/api/v0' (dev/test)
   * @example 'https://optimastride.com/api/v0' (prod)
   */
  readonly platformApiUrl: string;

  /**
   * Benzaiten application configuration
   */
  hayagriva: {
    appServer: {
      production: boolean;
      /**
       * Base URL for Benzaiten app server - ALWAYS HTTPS in production
       * DO NOT PUT SLASH IN THE END
       * @example 'http://localhost:3000' (dev/test)
       * @example 'https://optimastride.com' (prod)
       */
      baseURL: string;
      /**
       * API extension path
       * DO NOT PUT ANY SLASHES
       * @example 'api'
       */
      extension: string;
      /**
       * API version
       * DO NOT PUT ANY SLASHES
       * @example 'v0'
       */
      version: string;
      uploadFile?: string;
    };
    headers: {
      tenantuuid: string;
      appid: string;
      userToken: string;
    };
  };

  whiteboard?: {
    useYjsRealtime: boolean;
    yjsWsUrl: string;
  };
}

/**
 * Type guard to check if a URL is HTTPS
 */
export function isHttpsUrl(url: string): boolean {
  return url.startsWith('https://');
}

/**
 * Type guard to check if a URL is localhost
 */
export function isLocalhostUrl(url: string): boolean {
  return url.includes('localhost') || url.includes('127.0.0.1');
}

/**
 * Validates that HTTPS APIs are not using localhost
 * @throws Error if validation fails
 */
export function validateEnvironment(env: Environment): void {
  // Validate platformApiUrl is always HTTPS
  if (!isHttpsUrl(env.platformApiUrl)) {
    throw new Error(
      `Platform API URL must use HTTPS. Found: ${env.platformApiUrl}`
    );
  }

  // Validate platformApiUrl does not use localhost
  if (isLocalhostUrl(env.platformApiUrl)) {
    throw new Error(
      `Platform API URL cannot use localhost. Found: ${env.platformApiUrl}`
    );
  }

  // In production, ensure no localhost URLs
  if (env.production) {
    const localhostUrls: string[] = [];
    
    if (isLocalhostUrl(env.apiUrl)) localhostUrls.push('apiUrl');
    if (isLocalhostUrl(env.collaboraUrl)) localhostUrls.push('collaboraUrl');
    if (isLocalhostUrl(env.wopiUrl)) localhostUrls.push('wopiUrl');
    if (isLocalhostUrl(env.whiteboardProxyUrl)) localhostUrls.push('whiteboardProxyUrl');
    if (isLocalhostUrl(env.hayagriva.appServer.baseURL)) localhostUrls.push('hayagriva.appServer.baseURL');

    if (localhostUrls.length > 0) {
      console.warn(
        `Production environment contains localhost URLs: ${localhostUrls.join(', ')}`
      );
    }
  }

  // Validate hayagriva baseURL in production
  if (env.production && !isHttpsUrl(env.hayagriva.appServer.baseURL)) {
    console.warn(
      `Benzaiten baseURL should use HTTPS in production. Found: ${env.hayagriva.appServer.baseURL}`
    );
  }

  // Validate no trailing slashes in hayagriva URLs
  if (env.hayagriva.appServer.baseURL.endsWith('/')) {
    throw new Error(
      `hayagriva baseURL should not end with a slash. Found: ${env.hayagriva.appServer.baseURL}`
    );
  }

  // Validate no slashes in extension or version
  if (env.hayagriva.appServer.extension.includes('/')) {
    throw new Error(
      `hayagriva extension should not contain slashes. Found: ${env.hayagriva.appServer.extension}`
    );
  }

  if (env.hayagriva.appServer.version.includes('/')) {
    throw new Error(
      `hayagriva version should not contain slashes. Found: ${env.hayagriva.appServer.version}`
    );
  }
}
